import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Table, Input, Button, Breadcrumb, Tag, Space, DatePicker, Grid, Card, Row, Col,
  Statistic, Avatar, Dropdown, Tooltip, Select, message
} from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import {
  EyeOutlined, FileExcelOutlined, FilePdfOutlined, DownloadOutlined, SearchOutlined,
  RightOutlined, TeamOutlined, CheckCircleOutlined, CloseCircleOutlined,
  DollarOutlined, CalendarOutlined, FileTextOutlined, ClockCircleOutlined,
  UserOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { souscripteurService } from '../../common/services';

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;
const { Option } = Select;

const statusConfig = {
  active: { color: 'green', text: 'Active', icon: <CheckCircleOutlined /> },
  terminee: { color: 'default', text: 'Terminée', icon: <CheckCircleOutlined /> },
  annulee: { color: 'red', text: 'Annulée', icon: <CloseCircleOutlined /> }
};

const getStatusConfig = (status) => {
  return statusConfig[status] || { color: 'default', text: status || '—', icon: null };
};

const frequenceColors = {
  mensuel: '#1890ff',
  hebdomadaire: '#52c41a',
  journalier: '#faad14',
  trimestriel: '#722ed1',
  annuel: '#eb2f96'
};

const getAvatarColor = (name) => {
  const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068', '#1890ff', '#eb2f96', '#52c41a'];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (firstName, lastName) => {
  const f = (firstName || '').charAt(0).toUpperCase();
  const l = (lastName || '').charAt(0).toUpperCase();
  return f + l || 'U';
};

const Souscripteurs = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(undefined);
  const [siteId, setSiteId] = useState(undefined);
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('-createdAt');

  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'small' : 'middle';

  const formatMoney = (v) =>
    (Number(v || 0)).toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' FCFA';

  const fetchList = async () => {
    try {
      setLoading(true);
      const data = await souscripteurService.getList({
        page,
        limit,
        sort,
        search: search || undefined,
        status: status || undefined,
        siteId: siteId || undefined,
      });
      setRows(data?.data || []);
      setTotal(data?.total || 0);
    } catch (e) {
      console.error('Erreur liste souscripteurs:', e);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [page, limit, sort, search, status, siteId]);

  // Statistics calculation
  const stats = useCallback(() => {
    const totalSouscripteurs = total;
    const actifs = rows.filter(r => r.status === 'active').length;
    const termines = rows.filter(r => r.status === 'terminee').length;
    const totalPaye = rows.reduce((sum, r) => sum + (Number(r.paidTotal) || 0), 0);
    const totalRestant = rows.reduce((sum, r) => sum + (Number(r.remaining) || 0), 0);
    return { totalSouscripteurs, actifs, termines, totalPaye, totalRestant };
  }, [rows, total])();

  const columns = useMemo(
    () => [
      {
        title: 'Souscripteur',
        key: 'user',
        render: (_, r) => {
          const fullName = `${r.user?.firstName || ''} ${r.user?.lastName || ''}`.trim();
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar
                size={40}
                style={{ backgroundColor: getAvatarColor(fullName), fontWeight: 600 }}
              >
                {getInitials(r.user?.firstName, r.user?.lastName)}
              </Avatar>
              <div>
                <div style={{ fontWeight: 500, color: '#262626' }}>{fullName || 'Sans nom'}</div>
                <Tooltip title={r.user?.email}>
                  <div style={{ fontSize: 12, color: '#8c8c8c' }}>{r.user?.email || '—'}</div>
                </Tooltip>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>{r.user?.phone || '—'}</div>
              </div>
            </div>
          );
        },
      },
      {
        title: 'Site',
        dataIndex: ['site', 'nom'],
        key: 'site',
        render: (text) => (
          <Tag color="orange" style={{ borderRadius: 8 }}>{text || '—'}</Tag>
        ),
      },
      {
        title: 'Lot',
        key: 'lot',
        render: (_, r) => (
          <span style={{ fontWeight: 500 }}>{r.lot?.intitule || '—'}</span>
        ),
      },
      {
        title: 'Fréquence',
        dataIndex: 'frequence',
        key: 'frequence',
        render: (f) => (
          <Tag
            color={frequenceColors[f?.toLowerCase()] || '#1890ff'}
            style={{ borderRadius: 12, textTransform: 'capitalize' }}
          >
            {f || '—'}
          </Tag>
        ),
      },
      {
        title: 'Total',
        dataIndex: 'total',
        key: 'total',
        align: 'right',
        sorter: true,
        render: (v) => <span style={{ fontWeight: 500 }}>{formatMoney(v)}</span>,
      },
      {
        title: 'Payé',
        dataIndex: 'paidTotal',
        key: 'paidTotal',
        align: 'right',
        sorter: true,
        render: (v) => <span style={{ color: '#52c41a', fontWeight: 500 }}>{formatMoney(v)}</span>,
      },
      {
        title: 'Reste',
        dataIndex: 'remaining',
        key: 'remaining',
        align: 'right',
        sorter: true,
        render: (v) => (
          <span style={{ fontWeight: 600, color: v > 0 ? '#fa8c16' : '#52c41a' }}>
            {formatMoney(v)}
          </span>
        ),
      },
      {
        title: 'Statut',
        dataIndex: 'status',
        key: 'status',
        render: (s) => {
          const config = getStatusConfig(s);
          return (
            <Tag
              color={config.color}
              icon={config.icon}
              style={{ borderRadius: 12, padding: '2px 10px' }}
            >
              {config.text}
            </Tag>
          );
        },
      },
      {
        title: 'Prochaine échéance',
        key: 'next',
        render: (_, r) => {
          const date = r?.nextInstallment?.date;
          if (!date) return <span style={{ color: '#bfbfbf' }}>—</span>;
          const d = dayjs(date);
          const isClose = d.diff(dayjs(), 'day') <= 7;
          return (
            <Tooltip title={d.format('DD/MM/YYYY HH:mm')}>
              <Tag
                color={isClose ? 'red' : 'blue'}
                icon={<CalendarOutlined />}
                style={{ borderRadius: 8 }}
              >
                {d.format('DD/MM/YYYY')}
              </Tag>
            </Tooltip>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 80,
        render: (_, r) => (
          <Tooltip title="Voir les détails">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/dashboard/souscripteur/${r._id}`)}
              style={{
                borderRadius: 8,
                background: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)',
                border: 'none'
              }}
            />
          </Tooltip>
        ),
      },
    ],
    [navigate]
  );

  const onTableChange = (pagination, _filters, sorter) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);

    if (sorter?.columnKey) {
      const field = sorter.columnKey;
      const dir = sorter.order === 'ascend' ? '' : '-';
      if (['total', 'paidTotal', 'remaining'].includes(field)) {
        setSort(`${dir}${field}`);
      } else {
        setSort(`${dir}createdAt`);
      }
    }
  };

  // Export functions
  const exportToExcel = () => {
    message.loading('Export Excel en cours...', 1);
    const flat = rows.map((r) => ({
      Nom: `${r.user?.firstName || ''} ${r.user?.lastName || ''}`.trim(),
      Email: r.user?.email || '',
      Téléphone: r.user?.phone || '',
      Site: r.site?.nom || '',
      Lot: r.lot?.intitule || '',
      Fréquence: r.frequence || '',
      Total: r.total || 0,
      Payé: r.paidTotal || 0,
      Reste: r.remaining || 0,
      Statut: r.status || '',
      'Prochaine échéance': r?.nextInstallment?.date
        ? dayjs(r.nextInstallment.date).format('DD/MM/YYYY')
        : '',
      'Créé le': r.createdAt ? dayjs(r.createdAt).format('DD/MM/YYYY HH:mm') : '',
    }));
    const ws = XLSX.utils.json_to_sheet(flat);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Souscripteurs');
    XLSX.writeFile(wb, `souscripteurs_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    message.success('Export Excel terminé');
  };

  const exportToCSV = () => {
    message.loading('Export CSV en cours...', 1);
    const flat = rows.map((r) => ({
      Nom: `${r.user?.firstName || ''} ${r.user?.lastName || ''}`.trim(),
      Email: r.user?.email || '',
      Téléphone: r.user?.phone || '',
      Site: r.site?.nom || '',
      Lot: r.lot?.intitule || '',
      Fréquence: r.frequence || '',
      Total: r.total || 0,
      Payé: r.paidTotal || 0,
      Reste: r.remaining || 0,
      Statut: r.status || '',
    }));
    const ws = XLSX.utils.json_to_sheet(flat);
    const csv = XLSX.utils.sheet_to_csv(ws);
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `souscripteurs_${dayjs().format('YYYY-MM-DD')}.csv`);
    message.success('Export CSV terminé');
  };

  const exportToPDF = () => {
    message.loading('Export PDF en cours...', 1);
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Liste des Souscripteurs', 14, 15);
    doc.setFontSize(10);
    doc.text(`Export: ${dayjs().format('DD/MM/YYYY HH:mm')}`, 14, 22);
    autoTable(doc, {
      startY: 30,
      head: [['Souscripteur', 'Site', 'Lot', 'Total', 'Payé', 'Reste', 'Statut']],
      body: rows.map((r) => [
        `${r.user?.firstName || ''} ${r.user?.lastName || ''}`.trim(),
        r.site?.nom || '—',
        r.lot?.intitule || '—',
        (r.total || 0).toLocaleString('fr-FR'),
        (r.paidTotal || 0).toLocaleString('fr-FR'),
        (r.remaining || 0).toLocaleString('fr-FR'),
        getStatusConfig(r.status).text,
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [250, 140, 22] }
    });
    doc.save(`souscripteurs_${dayjs().format('YYYY-MM-DD')}.pdf`);
    message.success('Export PDF terminé');
  };

  const exportMenuItems = [
    { key: 'excel', icon: <FileExcelOutlined style={{ color: '#52c41a' }} />, label: 'Exporter en Excel', onClick: exportToExcel },
    { key: 'csv', icon: <FileTextOutlined style={{ color: '#1890ff' }} />, label: 'Exporter en CSV', onClick: exportToCSV },
    { key: 'pdf', icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />, label: 'Exporter en PDF', onClick: exportToPDF }
  ];

  return (
    <div className="content-wrapper">
      <style>
        {`
          .souscripteurs-table .ant-table-row:nth-child(odd) { background-color: #fafafa; }
          .souscripteurs-table .ant-table-row:hover > td { background-color: #fff7e6 !important; }
          .souscripteurs-table .ant-table-thead > tr > th { background: #fafafa; font-weight: 600; color: #595959; }
          .stat-card-sousc { border-radius: 12px; transition: all 0.3s ease; }
          .stat-card-sousc:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
        {/* Breadcrumb */}
        <Breadcrumb
          separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
          style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
        >
          <Breadcrumb.Item>
            <Link to="/dashboard" style={{ color: '#fa8c16' }}>Accueil</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span style={{ color: '#595959' }}>Souscripteurs</span>
          </Breadcrumb.Item>
        </Breadcrumb>

        {/* Statistics Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={8} lg={4}>
            <Card className="stat-card-sousc" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }} bodyStyle={{ padding: 16 }}>
              <Statistic
                title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>Total</span>}
                value={stats.totalSouscripteurs}
                valueStyle={{ color: 'white', fontSize: isMobile ? 20 : 24, fontWeight: 700 }}
                prefix={<UserOutlined style={{ marginRight: 4 }} />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Card className="stat-card-sousc" style={{ background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)', border: 'none' }} bodyStyle={{ padding: 16 }}>
              <Statistic
                title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>Actifs</span>}
                value={stats.actifs}
                valueStyle={{ color: 'white', fontSize: isMobile ? 20 : 24, fontWeight: 700 }}
                prefix={<CheckCircleOutlined style={{ marginRight: 4 }} />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Card className="stat-card-sousc" style={{ background: 'linear-gradient(135deg, #8c8c8c 0%, #bfbfbf 100%)', border: 'none' }} bodyStyle={{ padding: 16 }}>
              <Statistic
                title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>Terminés</span>}
                value={stats.termines}
                valueStyle={{ color: 'white', fontSize: isMobile ? 20 : 24, fontWeight: 700 }}
                prefix={<ClockCircleOutlined style={{ marginRight: 4 }} />}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} lg={5}>
            <Card className="stat-card-sousc" style={{ background: 'linear-gradient(135deg, #13c2c2 0%, #36cfc9 100%)', border: 'none' }} bodyStyle={{ padding: 16 }}>
              <Statistic
                title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>Total Payé</span>}
                value={stats.totalPaye}
                valueStyle={{ color: 'white', fontSize: isMobile ? 16 : 18, fontWeight: 700 }}
                suffix={<span style={{ fontSize: 12 }}>FCFA</span>}
                formatter={(value) => value.toLocaleString('fr-FR')}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={7}>
            <Card className="stat-card-sousc" style={{ background: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)', border: 'none' }} bodyStyle={{ padding: 16 }}>
              <Statistic
                title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>Reste à payer</span>}
                value={stats.totalRestant}
                valueStyle={{ color: 'white', fontSize: isMobile ? 16 : 18, fontWeight: 700 }}
                suffix={<span style={{ fontSize: 12 }}>FCFA</span>}
                formatter={(value) => value.toLocaleString('fr-FR')}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters and Table */}
        <Card style={{ borderRadius: 12 }}>
          {/* Filters */}
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Rechercher (nom, email, tél.)"
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                allowClear
                size={controlSize}
                style={{ borderRadius: 8 }}
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Select
                placeholder="Filtrer par statut"
                allowClear
                size={controlSize}
                style={{ width: '100%' }}
                onChange={(value) => { setStatus(value); setPage(1); }}
                value={status}
              >
                <Option value="active"><Tag color="green" style={{ marginRight: 4 }}>●</Tag> Active</Option>
                <Option value="terminee"><Tag color="default" style={{ marginRight: 4 }}>●</Tag> Terminée</Option>
                <Option value="annulee"><Tag color="red" style={{ marginRight: 4 }}>●</Tag> Annulée</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={7}>
              <RangePicker
                onChange={(r) => setDateRange(r)}
                placeholder={['Date début', 'Date fin']}
                format="DD/MM/YYYY"
                allowClear
                size={controlSize}
                style={{ width: '100%', borderRadius: 8 }}
              />
            </Col>
            <Col xs={24} sm={12} md={6} style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end', gap: 8 }}>
              <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                <Button icon={<DownloadOutlined />} size={controlSize} style={{ borderRadius: 8 }}>
                  Exporter
                </Button>
              </Dropdown>
            </Col>
          </Row>

          {/* Table */}
          <Table
            className="souscripteurs-table"
            rowKey="_id"
            columns={columns}
            dataSource={rows}
            loading={loading}
            onChange={onTableChange}
            size={controlSize}
            pagination={{
              current: page,
              pageSize: limit,
              total,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50, 100],
              showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} souscripteurs`
            }}
            scroll={{ x: 1200 }}
            locale={{ emptyText: 'Aucun souscripteur trouvé' }}
          />
        </Card>
      </div>
    </div>
  );
};

export default Souscripteurs;
